import { c as _c } from "react/compiler-runtime";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Skeleton for CostTable (default costs + add-ons grid).
 */
export function CostTableSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="mb-3 h-4 w-28" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div>{t0}<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{Array.from({
          length: 4
        }).map(_temp)}</div></div>;
    t2 = <Skeleton className="mb-3 h-4 w-32" />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="animate-in fade-in space-y-6 duration-300">{t1}<div>{t2}<div className="space-y-3">{Array.from({
            length: 3
          }).map(_temp3)}</div></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/**
 * Skeleton for CostCardGrid — mirrors the defaults + categorized add-ons layout.
 */
function _temp3(__0, i_0) {
  return <div key={i_0} className="border-ds-border-default overflow-hidden rounded-lg border"><div className="bg-ds-surface-muted border-ds-border-default border-b px-4 py-2"><Skeleton className="h-4 w-24" /></div><div className="divide-ds-border-default bg-ds-surface-card divide-y">{Array.from({
        length: 3
      }).map(_temp2)}</div></div>;
}
function _temp2(__, j) {
  return <div key={j} className="flex items-center justify-between px-4 py-3"><Skeleton className="h-4 w-48" /><Skeleton className="h-4 w-16" /></div>;
}
function _temp(_, i) {
  return <div key={i} className="rounded-ds-md shadow-ds-soft border-ds-border-default bg-ds-surface-card border px-4 py-3"><Skeleton className="mb-2 h-3 w-20" /><Skeleton className="h-6 w-16" /></div>;
}
export function CostCardGridSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="mb-3 h-4 w-28" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="animate-in fade-in space-y-6 duration-300"><div>{t0}<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{Array.from({
            length: 4
          }).map(_temp4)}</div></div>{Array.from({
        length: 2
      }).map(_temp6)}</div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Skeleton for UsageChart (stacked bar chart placeholder).
 */
function _temp6(__0, section) {
  return <div key={section}><div className="mb-3 flex items-center gap-2"><Skeleton className="h-4 w-4 rounded" /><Skeleton className="h-4 w-28" /><Skeleton className="h-5 w-12 rounded-full" /></div><div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{Array.from({
        length: 6
      }).map(_temp5)}</div></div>;
}
function _temp5(__1, i_0) {
  return <div key={i_0} className="rounded-ds-md shadow-ds-soft border-ds-border-default bg-ds-surface-card flex items-center justify-between border px-4 py-3"><div className="flex-1"><Skeleton className="mb-1 h-3 w-20" /><Skeleton className="h-4 w-32" /></div><Skeleton className="h-5 w-14" /></div>;
}
function _temp4(_, i) {
  return <div key={i} className="rounded-ds-md shadow-ds-soft border-ds-border-default bg-ds-surface-card border px-4 py-3"><Skeleton className="mb-2 h-3 w-20" /><Skeleton className="h-6 w-16" /></div>;
}
export function UsageChartSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute inset-x-2 bottom-8 top-3 flex items-end justify-between gap-1">{Array.from({
        length: 14
      }).map(_temp7)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="animate-in fade-in h-64 w-full duration-300"><div className="relative h-full w-full">{t0}<div className="absolute inset-x-2 bottom-0 flex justify-between">{Array.from({
            length: 5
          }).map(_temp8)}</div></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Composite skeleton for BillingClient initial load.
 * Renders: title + summary stats + chart + tabs + content preview.
 */
function _temp8(__0, i_0) {
  return <Skeleton key={i_0} className="h-3 w-10" />;
}
function _temp7(_, i) {
  return <Skeleton key={i} className="flex-1 rounded-sm" style={{
    height: `${30 + i * 17 % 70}%`
  }} />;
}
export function BillingClientSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mb-6"><Skeleton className="mb-2 h-8 w-56" /><Skeleton className="h-4 w-96" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">{Array.from({
        length: 3
      }).map(_temp9)}</div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="border-ds-border-default bg-ds-surface-card mb-6 rounded-xl border p-6"><div className="mb-4 flex items-center justify-between"><Skeleton className="h-5 w-28" /><Skeleton className="h-8 w-32 rounded-lg" /></div><UsageChartSkeleton /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="animate-in fade-in font-geist relative pr-0 duration-300 lg:pr-[42px]">{t0}{t1}{t2}<div className="border-ds-border-default mb-6 flex gap-4 border-b">{Array.from({
          length: 4
        }).map(_temp10)}</div><CostCardGridSkeleton /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp10(__0, i_0) {
  return <Skeleton key={i_0} className="h-10 w-28" />;
}
function _temp9(_, i) {
  return <div key={i} className="border-ds-border-default bg-ds-surface-card rounded-xl border p-6"><Skeleton className="mb-3 h-3 w-24" /><Skeleton className="mb-2 h-8 w-32" /><Skeleton className="h-2 w-full rounded-full" /></div>;
}